import React, { useRef } from "react";
import { FaInstagram } from "react-icons/fa";
import { openExternal } from "../lib/openExternal";
import type { User } from "../types/User";
import "./ProfileHeadRow.css";
import { t } from "../i18n";
import { useFitText } from "../hooks/useFitText";
import VerifiedTick from "./VerifiedTick";

// The top of the Profile page in edit mode: bio bubble, then one card — the
// avatar with the Live dot and the Instagram badge, the name, and "View
// profile" (founder, 1.5.2: the Spotify account card). The whole card opens
// the profile drawer, where the email and "Preview my public page" now live.
// Split out of pages/ProfilePage.tsx.

type Props = {
  bio: string;
  avatarSrc: string;
  defaultAvatarPath: string;
  isAccepting: boolean;
  user: User | null;
  name: string;
  instagram: string;
  /** The green tick beside the name (utils/talentTick). */
  verified?: boolean;
  onEdit: () => void;
};

export default function ProfileHeadRow({ bio, avatarSrc, defaultAvatarPath, isAccepting, user, name, instagram, verified = false, onEdit }: Props) {
  // The name keeps the card's size when it fits; a longer one shrinks, down
  // to 15 px, then ends in "…" (founder, 1.5.2 b48).
  const nameRef = useRef<HTMLHeadingElement>(null);
  useFitText(nameRef, name, 15);
  return (
    <>
      {/* Bio bubble — full row to itself (founder: the corner
          Preview pill reverted to the full-width button below). Wraps to
          max 2 lines so a full 40-char bio is always visible. */}
      <button
        type="button"
        className={`profile-bio-bubble${bio ? "" : " profile-bio-bubble--empty"}`}
        onClick={onEdit}
        title={t("profileHeadRow.editBio")}
      >
        <span className="profile-bio-bubble-text">{bio || t("profileHeadRow.tapToAddYour")}</span>
      </button>

      {/* The whole card is one tap target for the profile drawer
          (founder 2026-08-06) — the camera badge moved inside the drawer.
          div+role, not <button>: the IG chip is a nested link. */}
      <div
        className="profile-head-row profile-head-card"
        role="button"
        tabIndex={0}
        aria-label={t("profileHeadRow.viewProfile")}
        onClick={onEdit}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onEdit();
          }
        }}
      >
        <div className="profile-avatar-wrapper">
          <img
            src={avatarSrc}
            alt={t("profileHeadRow.avatar")}
            className="profile-avatar-image"
            onError={(e) => (e.currentTarget.src = defaultAvatarPath)}
          />

          {/* Online dot — reads the existing Live status (green = accepting
              requests, grey = Offline). Display only; the switch lives in
              the floating Live pill / Preferences. */}
          <span
            className={`profile-status-dot${isAccepting ? "" : " profile-status-dot--off"}`}
            aria-hidden="true"
          />

          {/* Instagram on the photo's edge, the fan page's badge (founder,
              1.5.2 b48): it took the QR badge's corner — the QR has its own
              tab — and left the whole line to the name. Nested inside the
              drawer tap target, so stop the tap. */}
          {instagram && (
            <a
              href={`https://instagram.com/${instagram}`}
              className="profile-ig-badge"
              title={`@${instagram}`}
              aria-label={t("profileHeadRow.instagram")}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                openExternal(`https://instagram.com/${instagram}`);
              }}
            >
              <FaInstagram size={13} />
            </a>
          )}
        </div>

        <div className="user-info">
          <div className="profile-head-name">
            <h2 ref={nameRef} title={name}>{name}</h2>
            {verified && <VerifiedTick className="profile-head-tick" label={t("editProfileSheet.verified").trim()} />}
          </div>
          <span className="profile-view-link">{t("profileHeadRow.viewProfile")}</span>
        </div>
      </div>
    </>
  );
}
